import React, { useState, useRef, useCallback } from 'react';
import { Button, Icon } from 'semantic-ui-react';

// CSS styles as a JavaScript object
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    backgroundColor: '#f9fafb'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
  },
  toolbarTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937'
  },
  toolbarControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  toolbarButton: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: '#4b5563',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  toolbarButtonHover: {
    backgroundColor: '#dbeafe',
    color: '#2563eb'
  },
  sizeIndicator: {
    fontSize: '14px',
    color: '#6b7280',
    marginLeft: '8px'
  },
  splitContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  panel: {
    overflow: 'auto',
    position: 'relative'
  },
  leftPanel: {
    backgroundColor: '#f8fafc',
    borderRight: '1px solid #d1d5db'
  },
  middlePanel: {
    backgroundColor: 'white',
    borderRight: '1px solid #d1d5db'
  },
  rightPanel: {
    backgroundColor: '#fafafa'
  },
  resizer: {
    width: '4px',
    cursor: 'col-resize',
    position: 'relative',
    transition: 'background-color 0.2s',
    userSelect: 'none',
    flexShrink: 0
  },
  resizerDefault: {
    backgroundColor: '#d1d5db'
  },
  resizerHover: {
    backgroundColor: '#60a5fa'
  },
  resizerActive: {
    backgroundColor: '#3b82f6'
  },
  resizerHandle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '12px',
    height: '32px',
    backgroundColor: '#9ca3af',
    borderRadius: '2px',
    opacity: 0,
    transition: 'opacity 0.2s'
  },
  resizerHandleVisible: {
    opacity: 1,
    backgroundColor: '#3b82f6'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    borderTop: '1px solid #e5e7eb',
    fontSize: '14px',
    color: '#4b5563'
  },
  // Panel content styles
  panelContent: {
    padding: '24px'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px'
  },
  panelTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b'
  },
  fileTree: {
    marginBottom: '32px'
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginBottom: '2px'
  },
  fileItemDefault: {
    color: '#374151',
    backgroundColor: 'transparent'
  },
  fileItemHover: {
    backgroundColor: '#f3f4f6'
  },
  fileItemActive: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  infoBox: {
    marginTop: '32px',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  codeEditor: {
    backgroundColor: '#111827',
    color: '#10b981',
    padding: '16px',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '14px',
    overflow: 'auto',
    marginBottom: '24px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginTop: '24px'
  },
  card: {
    padding: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px'
  },
  presetButton: {
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  presetButtonHover: {
    backgroundColor: '#e5e7eb',
    borderColor: '#9ca3af'
  }
};

const ThreePanelSplitView = ({ 
  leftPanel, 
  middlePanel,
  rightPanel,
  defaultLeftWidth = 15,
  defaultMiddleWidth = 20,
  minPanelWidth = 15,
  maxPanelWidth = 70,
  showToolbar = true,
  title = "Target",
  onResize = null,
  className = ""
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [middleWidth, setMiddleWidth] = useState(defaultMiddleWidth);
  const [activeResizer, setActiveResizer] = useState(null);
  const [hoveredResizer, setHoveredResizer] = useState(null);
  const containerRef = useRef(null);

  // Calculate right panel width
  const rightWidth = 100 - leftWidth - middleWidth;

  // Handle resizing for left resizer (between left and middle panels)
  const handleLeftResizerMouseDown = useCallback((e) => {
    setActiveResizer('left');
    e.preventDefault();

    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
        
        // Ensure minimum widths for all panels
        const maxLeft = Math.min(100 - middleWidth - minPanelWidth, maxPanelWidth);
        const constrainedLeftWidth = Math.min(Math.max(newLeftWidth, minPanelWidth), maxLeft);
        
        setLeftWidth(constrainedLeftWidth);
        
        if (onResize) {
          onResize(constrainedLeftWidth, middleWidth, 100 - constrainedLeftWidth - middleWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setActiveResizer(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [middleWidth, minPanelWidth, maxPanelWidth, onResize]);

  // Handle resizing for right resizer (between middle and right panels)
  const handleRightResizerMouseDown = useCallback((e) => {
    setActiveResizer('right');
    e.preventDefault();

    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const mousePercent = ((e.clientX - containerRect.left) / containerRect.width) * 100;
        const newMiddleWidth = mousePercent - leftWidth;
        
        // Ensure minimum widths for all panels
        const maxMiddle = Math.min(100 - leftWidth - minPanelWidth, maxPanelWidth);
        const constrainedMiddleWidth = Math.min(Math.max(newMiddleWidth, minPanelWidth), maxMiddle);
        
        setMiddleWidth(constrainedMiddleWidth);
        
        if (onResize) {
          onResize(leftWidth, constrainedMiddleWidth, 100 - leftWidth - constrainedMiddleWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setActiveResizer(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [leftWidth, minPanelWidth, maxPanelWidth, onResize]);

  // Preset layouts
  const setEqualWidths = () => {
    setLeftWidth(33.33);
    setMiddleWidth(33.33);
    if (onResize) onResize(33.33, 33.33, 33.34);
  };

  const setFocusMiddle = () => {
    setLeftWidth(15);
    setMiddleWidth(60);
    if (onResize) onResize(15, 60, 25);
  };

  const setWideLeft = () => {
    setLeftWidth(60);
    setMiddleWidth(25);
    if (onResize) onResize(60, 25, 15);
  };

  const setWideRight = () => {
    setLeftWidth(15);
    setMiddleWidth(25);
    if (onResize) onResize(15, 25, 60);
  };

  const resetToDefault = () => {
    setLeftWidth(defaultLeftWidth);
    setMiddleWidth(defaultMiddleWidth);
    if (onResize) onResize(defaultLeftWidth, defaultMiddleWidth, 100 - defaultLeftWidth - defaultMiddleWidth);
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {showToolbar && (
        <div style={styles.toolbar}>
          <h1 style={styles.toolbarTitle}>{title}</h1>
          <div style={styles.toolbarControls}>
            {/* Preset Layout Buttons */}
            <button 
              onClick={setEqualWidths}
              style={styles.presetButton}
              onMouseEnter={(e) => Object.assign(e.target.style, styles.presetButtonHover)}
              onMouseLeave={(e) => Object.assign(e.target.style, styles.presetButton)}
              title="Equal Widths"
            >
              Equal
            </button>
            <button 
              onClick={setFocusMiddle}
              style={styles.presetButton}
              onMouseEnter={(e) => Object.assign(e.target.style, styles.presetButtonHover)}
              onMouseLeave={(e) => Object.assign(e.target.style, styles.presetButton)}
              title="Focus Middle"
            >
              Focus Middle
            </button>
            <button 
              onClick={setWideLeft}
              style={styles.presetButton}
              onMouseEnter={(e) => Object.assign(e.target.style, styles.presetButtonHover)}
              onMouseLeave={(e) => Object.assign(e.target.style, styles.presetButton)}
              title="Wide Left"
            >
              Wide Left
            </button>
            <button 
              onClick={setWideRight}
              style={styles.presetButton}
              onMouseEnter={(e) => Object.assign(e.target.style, styles.presetButtonHover)}
              onMouseLeave={(e) => Object.assign(e.target.style, styles.presetButton)}
              title="Wide Right"
            >
              Wide Right
            </button>
            <Button circular icon='redo' size="tiny" onClick={resetToDefault}
              // style={styles.toolbarButton}
              // onMouseEnter={(e) => Object.assign(e.target.style, styles.toolbarButtonHover)}
              // onMouseLeave={(e) => Object.assign(e.target.style, styles.toolbarButton)} 
            />
          
            <div style={styles.sizeIndicator}>
              {Math.round(leftWidth)}% | {Math.round(middleWidth)}% | {Math.round(rightWidth)}%
            </div>
          </div>
        </div>
      )}

      <div style={styles.splitContainer}>
        {/* Left Panel */}
        <div 
          style={{
            ...styles.panel,
            ...styles.leftPanel,
            width: `${leftWidth}%`
          }}
        >
          {leftPanel || <></>}
        </div>

        {/* Left Resizer */}
        <div 
          style={{
            ...styles.resizer,
            ...(activeResizer === 'left' ? styles.resizerActive : 
                hoveredResizer === 'left' ? styles.resizerHover : styles.resizerDefault)
          }}
          onMouseDown={handleLeftResizerMouseDown}
          onMouseEnter={() => setHoveredResizer('left')}
          onMouseLeave={() => setHoveredResizer(null)}
        >
          <div style={{
            ...styles.resizerHandle,
            ...(hoveredResizer === 'left' ? styles.resizerHandleVisible : {})
          }}></div>
        </div>

        {/* Middle Panel */}
        <div 
          style={{
            ...styles.panel,
            ...styles.middlePanel,
            width: `${middleWidth}%`
          }}
        >
          {middlePanel || <></>}
        </div>

        {/* Right Resizer */}
        <div 
          style={{
            ...styles.resizer,
            ...(activeResizer === 'right' ? styles.resizerActive : 
                hoveredResizer === 'right' ? styles.resizerHover : styles.resizerDefault)
          }}
          onMouseDown={handleRightResizerMouseDown}
          onMouseEnter={() => setHoveredResizer('right')}
          onMouseLeave={() => setHoveredResizer(null)}
        >
          <div style={{
            ...styles.resizerHandle,
            ...(hoveredResizer === 'right' ? styles.resizerHandleVisible : {})
          }}></div>
        </div>

        {/* Right Panel */}
        <div 
          style={{
            ...styles.panel,
            ...styles.rightPanel,
            width: `${rightWidth}%`
          }}
        >
          {rightPanel || <></>}
        </div>
      </div>

      <div style={styles.statusBar}>
        <div>Three Panel Layout</div>
        <div>{activeResizer ? `Resizing ${activeResizer} divider...` : 'Ready'}</div>
      </div>
    </div>
  );
};



// Add global styles to ensure proper rendering
const globalStyles = document.createElement('style');
globalStyles.innerHTML = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  button:hover {
    opacity: 0.9;
  }
`;
document.head.appendChild(globalStyles);

export default ThreePanelSplitView;